/**
 * Hooks TanStack Query pour la récupération des données Road Alert
 */
import { useQuery, useMutation, useQueryClient, UseQueryOptions, QueryClient } from "@tanstack/react-query";
import {
  getCommunesByCriteria,
  getPrioriteByCriteria,
  getStatutAlerteByCriteria,
  getEquipesByCriteria,
  getChefEquipeByCriteria,
  getAlertesByCriteria,
  getAlerteNbreStat,
  getAlertesDocByCriteria,
  getAlertesDegradationByCriteria,
  getTypeTravauxByCriteria,
  getTypeChausseByCriteria,
  getTypeAlerteByCriteria,
  getDegradationByCriteria,
  getNiveauGraviteByCriteria,
  affectationEquipe,
  affectationEquipeMultiple,
  validerAlerteEnCours,
  getUsersByCriteria,
  getPointHebdomadaire,
  getItiSurfaceTraiteeByCriteria,
  getDepartementsByCriteria,
  getDepSurfaceTraiteeByCriteria,
  getCumulItineraireByCriteria,
  deleteAlerte,
} from "../../services/request-api";
import { formatCommunesResponse, formatListResponse } from "../../helpers/dataFormatter";

// Query keys
export const queryKeys = {
  communes: ["communes"] as const,
  priorites: ["priorites"] as const,
  statutAlertes: ["statutAlertes"] as const,
  equipes: ["equipes"] as const,
  chefEquipe: (idEquipe: string | number | null) => ["chefEquipe", idEquipe] as const,
  chefEquipes: ["chefEquipes"] as const,
  alertes: (params: { data?: Record<string, unknown>; page?: number; size?: number }) => ["alertes", params] as const,
  alerteNbreStat: (params: Record<string, unknown>) => ["alerteNbreStat", params] as const,
  alerte: (id: string) => ["alerte", id] as const,
  alertesDoc: (idAlerte: number) => ["alertesDoc", idAlerte] as const,
  alertesDegradation: (idAlerte: number) => ["alertesDegradation", idAlerte] as const,
  typeTravaux: ["typeTravaux"] as const,
  typeChausse: ["typeChausse"] as const,
  typeAlerte: ["typeAlerte"] as const,
  degradations: ["degradations"] as const,
  niveauGravite: ["niveauGravite"] as const,
  typeDegradation: ["typeDegradation"] as const,
  familleDegradation: ["familleDegradation"] as const,
  parametre: ["parametre"] as const,
  regions: ["regions"] as const,
  pointHebdomadaire: (params: Record<string, unknown>) => ["pointHebdomadaire", params] as const,
  itiSurfaceTraitee: (params: Record<string, unknown>) => ["itiSurfaceTraitee", params] as const,
  departements: ["departements"] as const,
  depSurfaceTraitee: (params: Record<string, unknown>) => ["depSurfaceTraitee", params] as const,
  cumulItineraire: (params: Record<string, unknown>) => ["cumulItineraire", params] as const,
};

/**
 * Invalide toutes les queries liées aux alertes (liste, détail, stats dashboard).
 * À appeler après création, modification, affectation, validation, etc.
 */
export function invalidateAlertesQueries(client: QueryClient): void {
  client.invalidateQueries({ queryKey: ["alertes"] });
  client.invalidateQueries({ queryKey: ["alerte"] });
  client.invalidateQueries({ queryKey: ["alerteNbreStat"] });
}

/**
 * Invalide toutes les queries liées aux paramètres (gravité, dégradations, régions, etc.).
 * À appeler après création, modification ou suppression dans la page Paramètres.
 */
export function invalidateParametresQueries(client: QueryClient): void {
  client.invalidateQueries({ queryKey: ["niveauGravite"] });
  client.invalidateQueries({ queryKey: ["typeDegradation"] });
  client.invalidateQueries({ queryKey: ["familleDegradation"] });
  client.invalidateQueries({ queryKey: ["degradations"] });
  client.invalidateQueries({ queryKey: ["parametre"] });
  client.invalidateQueries({ queryKey: ["typeChausse"] });
  client.invalidateQueries({ queryKey: ["typeAlerte"] });
  client.invalidateQueries({ queryKey: ["regions"] });
  client.invalidateQueries({ queryKey: ["departements"] });
}

/**
 * Invalide les queries de la page États (point hebdomadaire).
 * À appeler après recherche ou rafraîchissement.
 */
export function invalidateEtatsQueries(client: QueryClient): void {
  client.invalidateQueries({ queryKey: ["pointHebdomadaire"] });
}

/**
 * Invalide les queries de la page Exportation de données (surfaces itinéraire/département, cumul).
 * À appeler après recherche ou rafraîchissement.
 */
export function invalidateExportationDataQueries(client: QueryClient): void {
  client.invalidateQueries({ queryKey: ["itiSurfaceTraitee"] });
  client.invalidateQueries({ queryKey: ["depSurfaceTraitee"] });
  client.invalidateQueries({ queryKey: ["cumulItineraire"] });
}

/** Payload pour surfaces traitées par itinéraire */
export interface ItiSurfaceTraiteePayload {
  data: {
    idCommune?: string;
    dateCreationParam: {
      operator: "[]";
      start: string; // YYYY-MM-DD
      end: string;   // YYYY-MM-DD
    };
  };
}

export function useItiSurfaceTraiteeQuery(
  payload: ItiSurfaceTraiteePayload | null,
  options?: Partial<UseQueryOptions<unknown[], Error>>
) {
  return useQuery({
    queryKey: queryKeys.itiSurfaceTraitee(payload?.data ?? {}),
    queryFn: async () => {
      if (!payload?.data?.dateCreationParam) return [];
      const res = (await getItiSurfaceTraiteeByCriteria(payload as { data: Record<string, unknown> })) as any;
      if (Array.isArray(res)) return res;
      if (res?.items && Array.isArray(res.items)) return res.items;
      if (res?.content && Array.isArray(res.content)) return res.content;
      if (res?.data && Array.isArray(res.data)) return res.data;
      return [];
    },
    enabled: !!payload?.data?.dateCreationParam?.start && !!payload?.data?.dateCreationParam?.end,
    ...options,
  });
}

export function useDepartementsQuery() {
  return useQuery({
    queryKey: queryKeys.departements,
    queryFn: async () => {
      const res = await getDepartementsByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
  });
}

/** Payload pour surfaces traitées par département */
export interface DepSurfaceTraiteePayload {
  data: {
    idDepartement?: string;
    dateCreationParam: {
      operator: "[]";
      start: string; // YYYY-MM-DD
      end: string;   // YYYY-MM-DD
    };
  };
}

export function useDepSurfaceTraiteeQuery(
  payload: DepSurfaceTraiteePayload | null,
  options?: Partial<UseQueryOptions<unknown[], Error>>
) {
  return useQuery({
    queryKey: queryKeys.depSurfaceTraitee(payload?.data ?? {}),
    queryFn: async () => {
      if (!payload?.data?.dateCreationParam) return [];
      const res = (await getDepSurfaceTraiteeByCriteria(payload as { data: Record<string, unknown> })) as any;
      if (Array.isArray(res)) return res;
      if (res?.items && Array.isArray(res.items)) return res.items;
      if (res?.content && Array.isArray(res.content)) return res.content;
      if (res?.data && Array.isArray(res.data)) return res.data;
      return [];
    },
    enabled: !!payload?.data?.dateCreationParam?.start && !!payload?.data?.dateCreationParam?.end,
    ...options,
  });
}

/** Payload pour cumul itinéraire */
export interface CumulItinerairePayload {
  data: {
    annee?: string;
    mois?: string; // Format "02d" (ex: "01", "02", etc.)
    statut?: string;
  };
}

export function useCumulItineraireQuery(
  payload: CumulItinerairePayload | null,
  options?: Partial<UseQueryOptions<unknown[], Error>>
) {
  return useQuery({
    queryKey: queryKeys.cumulItineraire(payload?.data ?? {}),
    queryFn: async () => {
      if (!payload) return [];
      const res = (await getCumulItineraireByCriteria(payload as { data: Record<string, unknown> })) as any;
      if (Array.isArray(res)) return res;
      if (res?.items && Array.isArray(res.items)) return res.items;
      if (res?.content && Array.isArray(res.content)) return res.content;
      if (res?.data && Array.isArray(res.data)) return res.data;
      return [];
    },
    enabled: !!payload,
    ...options,
  });
}

/** Payload pour point hebdomadaire (états) : idCommune, traitePar, idStatutAlerte, idPriorite, dateCreationParam[] */
export interface PointHebdomadairePayload {
  data: {
    idCommune?: string;
    traitePar?: string;
    idStatutAlerte?: string;
    idPriorite?: string;
    dateCreationParam: {
      operator: "[]";
      start: string; // YYYY-MM-DD
      end: string;   // YYYY-MM-DD
    };
  };
}

export function usePointHebdomadaireQuery(
  payload: PointHebdomadairePayload | null,
  options?: Partial<UseQueryOptions<unknown[], Error>>
) {
  return useQuery({
    queryKey: queryKeys.pointHebdomadaire(payload?.data ?? {}),
    queryFn: async () => {
      if (!payload?.data?.dateCreationParam) return [];
      const res = (await getPointHebdomadaire(payload as { data: Record<string, unknown> })) as any;
      if (Array.isArray(res)) return res;
      if (res?.items && Array.isArray(res.items)) return res.items;
      if (res?.content && Array.isArray(res.content)) return res.content;
      if (res?.data && Array.isArray(res.data)) return res.data;
      return [];
    },
    enabled: !!payload?.data?.dateCreationParam?.start && !!payload?.data?.dateCreationParam?.end,
    ...options,
  });
}

/** Extrait un tableau depuis une réponse API (data peut être un tableau ou un objet contenant mapList/items/content/data). */
function ensureArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    const v = value as Record<string, unknown>;
    if (Array.isArray(v.mapList)) return v.mapList;
    if (Array.isArray(v.items)) return v.items;
    if (Array.isArray(v.content)) return v.content;
    if (Array.isArray(v.data)) return v.data;
    if (Array.isArray(v.listmap)) return v.listmap;
  }
  return [];
}

export function useCommunesQuery() {
  return useQuery({
    queryKey: queryKeys.communes,
    queryFn: async () => {
      const res = await getCommunesByCriteria({ data: {} });
      const formatted = formatCommunesResponse(res);
      return ensureArray(formatted?.data);
    },
  });
}

export function usePrioritesQuery() {
  return useQuery({
    queryKey: queryKeys.priorites,
    queryFn: async () => {
      const res = await getPrioriteByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return ensureArray(formatted?.data);
    },
  });
}

export function useStatutAlertesQuery() {
  return useQuery({
    queryKey: queryKeys.statutAlertes,
    queryFn: async () => {
      const res = await getStatutAlerteByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return ensureArray(formatted?.data);
    },
  });
}

export function useEquipesQuery() {
  return useQuery({
    queryKey: queryKeys.equipes,
    queryFn: async () => {
      const res = await getEquipesByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
  });
}

export function useChefEquipeQuery(idEquipe: string | number | null) {
  return useQuery({
    queryKey: queryKeys.chefEquipe(idEquipe),
    queryFn: async () => {
      const res = await getUsersByCriteria({ data: { idEquipe: String(idEquipe) } });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
    enabled: !!idEquipe && idEquipe !== "",
  });
}

export function useChefEquipesQuery() {
  return useQuery({
    queryKey: queryKeys.chefEquipes,
    queryFn: async () => {
      const res = await getChefEquipeByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
  });
}

export interface AlertesQueryResult {
  items: unknown[];
  total: number;
}

export function useAlertesQuery(
  params: { user?: string; data: Record<string, unknown>; page?: number; size?: number },
  options?: Partial<UseQueryOptions<AlertesQueryResult, Error>>
) {
  return useQuery({
    queryKey: queryKeys.alertes(params),
    queryFn: async (): Promise<AlertesQueryResult> => {
      const res = await getAlertesByCriteria(params);
      const raw = res as unknown as Record<string, unknown> | undefined;
      let items: unknown[] = [];
      let total = 0;
      if (raw?.data && Array.isArray(raw.data)) {
        items = raw.data;
      } else if (Array.isArray(raw)) {
        items = raw;
      }
      // Total : priorité other.count (format API Road Alert), puis total, totalElements
      const other = raw?.other as { count?: number } | undefined;
      if (typeof other?.count === 'number') {
        total = other.count;
      } else if (typeof raw?.total === 'number') {
        total = raw.total;
      } else if (typeof (raw as Record<string, unknown>)?.totalElements === 'number') {
        total = (raw as Record<string, unknown>).totalElements as number;
      } else {
        total = items.length;
      }
      return { items, total };
    },
    ...options,
  });
}

export function useAlerteNbreStatQuery(
  payload: { user?: string; data: Record<string, unknown> } | null,
  options?: Partial<UseQueryOptions<unknown[], Error>>
) {
  return useQuery({
    queryKey: queryKeys.alerteNbreStat(payload?.data ?? {}),
    queryFn: async () => {
      if (!payload) return [];
      const res = await getAlerteNbreStat(payload);
      return res?.data ?? [];
    },
    enabled: (options?.enabled ?? !!payload),
    ...options,
  });
}

export function useAlerteDetailQueries(id: string | undefined) {
  const alertesQuery = useQuery({
    queryKey: queryKeys.alerte(id ?? ""),
    queryFn: async () => {
      const res = await getAlertesByCriteria({ data: { idAlerte: id } });
      const list = res?.data ?? [];
      return Array.isArray(list) ? list[0] ?? null : null;
    },
    enabled: !!id,
  });

  const degradationsQuery = useQuery({
    queryKey: queryKeys.alertesDegradation(Number(id ?? 0)),
    queryFn: async () => {
      const res = await getAlertesDegradationByCriteria({ data: { idAlerte: Number(id) } });
      return res?.data ?? [];
    },
    enabled: !!id && !Number.isNaN(Number(id)),
  });

  const documentsQuery = useQuery({
    queryKey: queryKeys.alertesDoc(Number(id ?? 0)),
    queryFn: async () => {
      const res = await getAlertesDocByCriteria({ data: { idAlerte: Number(id) } });
      return res?.data ?? [];
    },
    enabled: !!id && !Number.isNaN(Number(id)),
  });

  return {
    alerte: alertesQuery.data ?? null,
    degradations: degradationsQuery.data ?? [],
    documents: documentsQuery.data ?? [],
    loading: alertesQuery.isLoading || degradationsQuery.isLoading || documentsQuery.isLoading,
    error: alertesQuery.error ?? degradationsQuery.error ?? documentsQuery.error,
    refetch: () => {
      alertesQuery.refetch();
      degradationsQuery.refetch();
      documentsQuery.refetch();
    },
  };
}

export function useAlertesDocQuery(idAlerte: number | null) {
  return useQuery({
    queryKey: queryKeys.alertesDoc(idAlerte ?? 0),
    queryFn: async () => {
      const res = await getAlertesDocByCriteria({ data: { idAlerte: idAlerte! } });
      return res?.data ?? [];
    },
    enabled: !!idAlerte && idAlerte > 0,
  });
}

export function useTypeTravauxQuery() {
  return useQuery({
    queryKey: queryKeys.typeTravaux,
    queryFn: async () => {
      const res = await getTypeTravauxByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
  });
}

export function useTypeChausseQuery() {
  return useQuery({
    queryKey: queryKeys.typeChausse,
    queryFn: async () => {
      const res = await getTypeChausseByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
  });
}

export function useTypeAlerteQuery() {
  return useQuery({
    queryKey: queryKeys.typeAlerte,
    queryFn: async () => {
      const res = await getTypeAlerteByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
  });
}

export function useDegradationsQuery() {
  return useQuery({
    queryKey: queryKeys.degradations,
    queryFn: async () => {
      const res = await getDegradationByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
  });
}

export function useNiveauGraviteQuery() {
  return useQuery({
    queryKey: queryKeys.niveauGravite,
    queryFn: async () => {
      const res = await getNiveauGraviteByCriteria({ data: {} });
      const formatted = formatListResponse(res);
      return formatted?.data ?? [];
    },
  });
}

/** Payload attendu par l'API affectationEquipe */
export interface AffectationEquipePayload {
  idAlerte: number;
  idStatutAlerte: number;
  idPriorite?: number;
  idTypeChausse?: number;
  nomVoie?: string;
  usernameModif: string;
  username: string;
  idEquipe: string;
  idChefEquipe: string;
}

export function useAffectationEquipeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AffectationEquipePayload) => {
      const response = await affectationEquipe({ data: { ...data } as Record<string, unknown> });
      // formatApiResponse retourne { success: boolean, message: string, ... }
      // Si success est false, lever une erreur avec le message
      if (response && typeof response === 'object' && 'success' in response && !response.success) {
        const errorMessage = response.message || "Erreur lors de l'affectation";
        throw new Error(errorMessage);
      }
      return response;
    },
    onSuccess: () => {
      invalidateAlertesQueries(queryClient);
    },
  });
}

/** Payload attendu par l'API affectationEquipemultiple */
export interface AffectationEquipeMultiplePayload {
  idPriorite?: string;
  idEquipe: string;
  idStatutAlerte: string;
  username: string; // Chef d'équipe
  usernameModif: string; // Utilisateur courant
  idChefEquipe: string;
  affecteAlerte: { idAlerte: string | number }[];
}

export function useAffectationEquipeMultipleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AffectationEquipeMultiplePayload) => {
      const response = await affectationEquipeMultiple({ data: { ...data } as Record<string, unknown> });
      if (response && typeof response === 'object' && 'success' in response && !response.success) {
        const errorMessage = response.message || "Erreur lors de l'affectation multiple";
        throw new Error(errorMessage);
      }
      return response;
    },
    onSuccess: () => {
      invalidateAlertesQueries(queryClient);
    },
  });
}

export function useValiderAlerteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (idAlerte: number) => {
      const response = await validerAlerteEnCours({
        data: { idAlerte, idStatutAlerte: 6 },
      });
      if (response && typeof response === "object" && "success" in response && !response.success) {
        throw new Error(response.message || "Erreur lors de la validation");
      }
      return response;
    },
    onSuccess: () => {
      invalidateAlertesQueries(queryClient);
    },
  });
}

export function useDeleteAlerteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (idAlerte: number | string) => {
      const response = await deleteAlerte({ data: { idAlerte } });
      if (response && typeof response === "object" && "success" in response && !response.success) {
        throw new Error(response.message || "Erreur lors de la suppression de l'alerte");
      }
      return response;
    },
    onSuccess: () => {
      invalidateAlertesQueries(queryClient);
    },
  });
}
